import { isUndefined, ToolkitPropertyEnum } from '@aloft/toolkit-shared';
import { Flex, theme } from '@aloft/toolkit-sui';

import { useGetListing } from '../../controller/hooks/listings/useGetListing.ts';
import { getReferenceProperty } from '../../modules/utils/getReferenceProperty.ts';
import {
  selectCompSequence,
  selectPropertyTitleById,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyIdentificationById } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import {
  selectToolkitListingID,
  selectTransactionById,
} from '../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { getColorForStatusOrSubject } from '../../utils/getColorForStatusOrSubject.ts';
import { getPropertyPinStatus } from '../../utils/getPropertyPinStatus.ts';
import { registerTkv2ReferenceProperty } from '../ReferenceBar/registerTkv2ReferenceProperty.ts';
import { ValuationTablePropertyHeaderActions } from './internal/ValuationTablePropertyHeaderActions.tsx';
import { ValuationTablePropertyHeaderAddress } from './internal/ValuationTablePropertyHeaderAddress.tsx';
import { ValuationTablePropertyHeaderColorBand } from './internal/ValuationTablePropertyHeaderColorBand.tsx';
import { ValuationTablePropertyHeaderImage } from './internal/ValuationTablePropertyHeaderImage.tsx';
import { ValuationTablePropertyHeaderPrice } from './internal/ValuationTablePropertyHeaderPrice.tsx';
import { ValuationTablePropertyHeaderTitle } from './internal/ValuationTablePropertyHeaderTitle.tsx';

type PropertyHeaderElementParams = {
  propertyId: string;
  hideActions?: boolean;
  showDataSourceDrawerToggler?: boolean;
};

export const CraftsmanPropertyHeader = ({
  propertyId,
  hideActions = false,
  showDataSourceDrawerToggler = false,
}: PropertyHeaderElementParams) => {
  const isSubject = ['subject', 'subjectArv'].includes(propertyId);
  const propertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, propertyId),
  );

  const title = useAppSelector((state) => selectPropertyTitleById(state, propertyId));
  const transaction = useAppSelector((state) => selectTransactionById(state, propertyId));
  const toolkitListingID = useAppSelector((state) => selectToolkitListingID(state, propertyId));
  const listing = useGetListing(toolkitListingID);
  const compSequence = useAppSelector(selectCompSequence);

  if (isUndefined(propertyIdentification) || isUndefined(transaction)) {
    return null;
  }

  let propertyStatus: ToolkitPropertyEnum<'ListingStatusType'> | 'Subject' | 'Subject (ARV)' = 'Subject';
  if (propertyId === 'subject') {
    propertyStatus = 'Subject';
  } else if (propertyId === 'subjectArv') {
    propertyStatus = 'Subject (ARV)';
  } else {
    propertyStatus = transaction.status ?? 'Off-Market';
  }

  const pinStatus = getPropertyPinStatus(propertyStatus);
  const pinColor = getColorForStatusOrSubject(propertyStatus);

  const referenceProperty = getReferenceProperty(propertyId, compSequence);
  const referenceBarProps = registerTkv2ReferenceProperty(referenceProperty);

  return (
    <Flex
      position="sticky"
      flexDirection="column"
      top="0"
      zIndex={theme.zIndices.sticky}
      backgroundColor="surface1"
      borderWidth="1px"
      boxShadow="0px 4px 8px 0px #0000000F"
      {...referenceBarProps}
    >
      <Flex>
        <ValuationTablePropertyHeaderImage
          propertyId={propertyId}
          propertyIdentification={propertyIdentification}
          listing={listing}
          hideActions={hideActions}
        />
        <Flex flexDirection="column" flexGrow={1} overflow="hidden" paddingTop="4px">
          <Flex gap="4px" flexGrow={1}>
            <Flex flexDirection="column" flexGrow={1} paddingLeft="6px" gap="4px">
              <ValuationTablePropertyHeaderTitle
                hideActions={hideActions}
                pinColor={pinColor}
                pinStatus={pinStatus}
                propertyId={propertyId}
                title={title}
              />
              <ValuationTablePropertyHeaderAddress address={propertyIdentification.address} />
              <ValuationTablePropertyHeaderPrice
                propertyId={propertyId}
                hideActions={hideActions}
                isSubject={isSubject}
                listing={listing}
                transaction={transaction}
                isFeatureGrid={showDataSourceDrawerToggler}
              />
            </Flex>
            {!hideActions && (
              <ValuationTablePropertyHeaderActions
                propertyId={propertyId}
                showDataSourceDrawerToggler={showDataSourceDrawerToggler}
                title={title}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      <ValuationTablePropertyHeaderColorBand color={pinColor} />
    </Flex>
  );
};
